import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem("golden-moon-cart");
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      return [];
    }
  });

  // Notification state
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    localStorage.setItem("golden-moon-cart", JSON.stringify(cartItems));
  }, [cartItems]);

  const showNotification = (productName) => {
    // Clear any existing notification
    setNotification(null);
    
    // Set new notification
    setNotification({
      id: Date.now(),
      productName: productName,
      message: "ajouté au panier"
    });

    // Auto hide after 2.5 seconds
    setTimeout(() => {
      setNotification(null);
    }, 2500);
  };

  const addToCart = (item) => {
    const uniqueId = `${item.id}-${item.frameColor}-${item.glassOption}-${item.customText}`;

    setCartItems((prev) => {
      const existing = prev.find((p) => p.uniqueId === uniqueId);

      if (existing) {
        const updated = prev.map((p) =>
          p.uniqueId === uniqueId
            ? { ...p, quantity: p.quantity + item.quantity }
            : p
        );
        showNotification(item.name);
        return updated;
      }

      showNotification(item.name);
      return [...prev, { ...item, uniqueId }];
    });
  };

  const removeFromCart = (uniqueId) => {
    setCartItems((prev) => prev.filter((item) => item.uniqueId !== uniqueId));
  };

  const updateQuantity = (uniqueId, quantity) => {
    if (quantity < 1) return;
    setCartItems((prev) =>
      prev.map((item) =>
        item.uniqueId === uniqueId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const cartCount = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + item.quantity, 0);
  }, [cartItems]);

  const subtotal = useMemo(() => {
    return cartItems.reduce(
      (acc, item) => acc + (item.basePrice + item.glassExtra) * item.quantity,
      0
    );
  }, [cartItems]);

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartCount,
    subtotal,
    notification
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used inside CartProvider");
  }
  return context;
}